import { useMemo, useCallback, useState } from 'react';
import { Activity } from '../../../../../../../../api/generated/data-contracts';
import { DirectionalHint, Dropdown, IDropdownOption, IDropdownProps, Stack, TextField, TooltipHost, IconButton } from '@fluentui/react';
import { ApiCalls } from '../../../../../../../../api/api';
import { useMutation } from '@tanstack/react-query';
import { useRowMetaContext } from '../../../../context/RowMetaContext';
import { TimeEntryApprovalDtoState, useActivityState, useApprovalState, useApprovalStore, useRateState } from '../../../../../../../../_grid/grid3';
import shallow from 'zustand/shallow';

export const ActivityDropdown = () => {
    const activities = useApprovalStore(store => Object.values(store.activities), shallow);
    const { row } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const [approvalState] = useApprovalState(row.id);
    const options = useMemo(
        (): IDropdownOption<Activity | null>[] =>
            activities.map(activity => {
                const currencyPart = activity.rateCurrencyCode ? ` ${activity.rateCurrencyCode}` : '';
                const text = activity.rate === null ? activity.name : `${activity.name} (${activity.rate.toFixed(2)}${currencyPart})`;
                return {
                    key: activity.id,
                    text: text,
                    data: activity,
                };
            }),
        [activities],
    );

    const [activity, setActivity] = useActivityState(row.id);
    const [showManualOverlay, setShowManualOverlay] = useState(false);
    const { mutate: mutateCustomRate } = useMutation((rate: number) => ApiCalls.setCustomRateForApproval({ rate, timeEntryId: row.timeEntry.id }));
    const { mutate: mutateActivity } = useMutation((selectedActivity: Activity) =>
        ApiCalls.setActivity({ selectedActivity, timeEntryId: row.timeEntry.id, approvalId: row.id }),
    );

    const [rate, setRate] = useRateState(row.id);

    const [, setApproval] = useApprovalState(row.id);

    const resetApprovalIfEditable = useCallback(() => {
        if (approvalState === 'Approved' && activity?.allowEditAfterApproval) {
            setApproval('Pending');
        }
    }, [approvalState, activity?.allowEditAfterApproval, setApproval]);

    const onChangeDropdown = useCallback(
        (e: any, option: IDropdownOption<Activity>) => {
            if (option) {
                const newActivity = option.data;
                const oldActivity = activity;
                if (oldActivity && newActivity && newActivity.id === oldActivity.id) {
                    return; // User has chosen the same non-custom activity, do nothing
                }
                resetApprovalIfEditable();
                mutateActivity(newActivity);
                setActivity(newActivity);
            }
        },
        [activity, mutateActivity, resetApprovalIfEditable, setActivity],
    );

    const [localRateState, setLocalRateState] = useState(String(rate));

    const onBlur = useCallback(() => {
        const newRate = Number(localRateState);
        setShowManualOverlay(false);
        if (!Number.isNaN(newRate)) {
            resetApprovalIfEditable();
            mutateCustomRate(newRate);
            setRate(newRate);
            setActivity(undefined);
        } else {
            setLocalRateState(String(rate));
        }
    }, [localRateState, mutateCustomRate, rate, resetApprovalIfEditable, setActivity, setRate]);

    return (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            {showManualOverlay ? (
                <TextField
                    autoFocus
                    styles={{ root: { width: 200 } }}
                    type="number"
                    onChange={(e, newValue) => {
                        setLocalRateState(newValue);
                    }}
                    value={String(localRateState || '')}
                    onBlur={onBlur}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            (e.target as HTMLDivElement).blur();
                        }
                    }}
                    placeholder="Enter custom rate"
                />
            ) : (
                <Dropdown
                    styles={dropdownStyles}
                    options={options}
                    selectedKey={activity?.id || null}
                    onChange={onChangeDropdown}
                    placeholder="Select rate"
                    disabled={approvalState === 'Approved' && !activity?.allowEditAfterApproval}
                />
            )}
            <TooltipHost content={`${showManualOverlay ? 'Save' : 'Set'} billable adjustment`} directionalHint={DirectionalHint.topLeftEdge} closeDelay={250}>
                {showManualOverlay ? (
                    <IconButton
                        //
                        key="save"
                        iconProps={{ iconName: 'Save' }}
                        disabled={approvalState === 'Approved' && !activity?.allowEditAfterApproval}
                        data-testid="save"
                        style={{ fontSize: 12, cursor: 'pointer' }}
                        onMouseUp={onBlur}
                    />
                ) : (
                    <IconButton
                        key="edit"
                        iconProps={{ iconName: 'Edit' }}
                        disabled={approvalState === 'Approved' && !activity?.allowEditAfterApproval}
                        data-testid="edit"
                        style={{ fontSize: 12, cursor: 'pointer' }}
                        onClick={() => setShowManualOverlay(true)}
                    />
                )}
            </TooltipHost>
            <TooltipHost content="Revert to default" directionalHint={DirectionalHint.topLeftEdge} closeDelay={250}>
                <IconButton
                    iconProps={{ iconName: 'RevToggleKey' }}
                    disabled={approvalState === 'Approved' && !activity?.allowEditAfterApproval}
                    data-testid="delete"
                    style={{ fontSize: 12, cursor: 'pointer' }}
                    onClick={() => {
                        resetApprovalIfEditable();
                        setRate(row.originalRate);
                        setActivity(undefined);
                        mutateActivity(null);
                    }}
                />
            </TooltipHost>
        </Stack>
    );
};

const dropdownStyles: IDropdownProps['styles'] = { root: { width: 200 }, callout: { minWidth: 'min-content' } };
